// @ts-strict-ignore
import { ActionType } from 'flow/store/engine/constants';
import type {
  Action, PopulatedThreshold,
  ServicesByName,
} from 'flow/store/engine/types';
import {
  getActionName,
  isActionOfType,
  getActionTypeText,
} from 'flow/service/actions';
import type { DropdownOption } from '../../NodeEditorDrawer/types';

import { getActionBorderBottom } from 'flow/editor/styles';

import { MULTISELECT_PROPS } from '../../constants';
import { RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import dayjs from 'dayjs';
import { canLinkToNode } from '../../Graph/nodes/BaseNode/NodeConnector/helpers';
import type { NodeLineage } from '~/dashboard/views/workflows/service/graph';
import { serviceHasAttributes } from '~/dashboard/views/workflows/service/services';
import { COMMA_DELINEATOR, COMMA_DELINEATOR_DISPLAY } from './constants';
import { BEST_EFFORT_PARSING_DEFAULT_STRING } from 'flow/service/thresholds';
import type { MultiSelectProps } from '@usealloy/component-library';

/* Generates action or mode-specific styles in a style object to feed into the react-select style functions
 */
export function getDropdownTextStyles(
  action: Action,
  isRegex: boolean,
  inlineSuggestion: boolean,
) {
  switch (action?.type) {
    case ActionType.SERVICE: {
      return { color: 'var(--green-green-550, #3A795F)' };
    }
    case ActionType.OUTPUT:
    case ActionType.INPUT:
    case ActionType.MATRIX_MODEL: {
      return { color: 'var(--cl-juicyBlue-500)' };
    }
    default:
      break;
  }

  if (inlineSuggestion) return { color: 'var(--cl-yellow-400)' };

  if (isRegex) return { color: 'var(--cl-pink-600)' };

  return { color: 'var(--cl-text-default)' };
}

export function getMultiSelectProps({
  action,
  mode,
  styleConditions,
}: {
  action: Action;
  mode: RuleEditorDropdownMode;
  styleConditions: {
    inlineSuggestion?: boolean;
    isPreview: boolean;
    isCTAOptionAvailable?: boolean;
    isRegex?: boolean;
    isSimpleEditorMode?: boolean;
  };
}): Partial<MultiSelectProps> {
  const {
    isPreview,
    isRegex,
    inlineSuggestion,
    isSimpleEditorMode,
  } = styleConditions;

  return {
    ...MULTISELECT_PROPS,
    customStyleFunctions: {
      ...MULTISELECT_PROPS.customStyleFunctions,
      container: () => ({
        margin: '0',
        opacity: isPreview ? '0.5' : '1',
      }),
      singleValue: () => ({
        borderTop: '2px solid transparent',
        borderBottom: getActionBorderBottom(action),
        fontFamily: 'var(--cl-font-family-mono)',
        fontSize: 'var(--cl-font-size-2)',
        fontWeight: 'var(--cl-font-weight-bold)',
        ...(getDropdownTextStyles(action, isRegex, inlineSuggestion)),
      }),
      valueContainer: () => ({
        padding: mode === 'caretExpression' ? '0 1px' : '0 3px',
        caretColor: 'var(--cl-yellow-500)',
      }),
      menuList: () => ({
      }),
      menu: () => ({
        position: 'absolute',
        right: isSimpleEditorMode ? '0' : 'unset',
        borderRadius: 'var(--cl-radius-3)',
        minWidth: '250px',
        maxWidth: '600px',
        width: 'max-content',
        boxShadow: '0px 2px 8px 0px rgba(0,0,0, 0.12)',
        border: 'none',
        overflow: 'hidden',
      }),
    },
  };
}

export function getSelectProps(
  isCTAOptionAvailable,
  mode,
  regexRightAttributeValueMode,
  simpleEditorMode,
  threshold,
) {
  const actionMap = {
    [RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION]: threshold?.left.action,
    [RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE]: threshold?.left.action,

    [RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION]: threshold?.right.action,
    [RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE]: threshold?.right.action,
  };

  return getMultiSelectProps({
    action: actionMap[mode],
    mode,
    styleConditions: {
      isCTAOptionAvailable,
      isPreview: threshold?.isPreview,
      isRegex: regexRightAttributeValueMode,
      isSimpleEditorMode: simpleEditorMode,
    },
  });
}

export function isPossibleLogicalGrouping({
  pendingGroupOpeningParenthesisPath,
  parentRulePath,
  mode,
}: {
  pendingGroupOpeningParenthesisPath: string;
  parentRulePath: string;
  mode: RuleEditorDropdownMode;
}) {
  // checks if pending group open parenthesis is not set yet
  // or pending group open parenthesis is opened in the current position ("here")
  // if it's opened "here", it'll display inline blinking parenthesis.
  // if pending group open paren path is not set at all, we can then show group open parenthesis caret here.
  // if pending group open paren path is set to somewhere but not "here", we can't allow user to open another opening parenthesis elsewhere
  const pendingGroupOpenParenOpenedHereOrNotSet = !pendingGroupOpeningParenthesisPath
    || pendingGroupOpeningParenthesisPath === parentRulePath;

  return (
    pendingGroupOpenParenOpenedHereOrNotSet
    && (
      mode === RuleEditorDropdownMode.GROUP_START
      || mode === RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION
    )
  );
}

export function getSelectedOptionValue({
  mode,
  threshold,
  leftDataType,
}: {
  mode: RuleEditorDropdownMode;
  threshold: PopulatedThreshold;
  leftDataType: string;
}) {
  switch (mode) {
    case RuleEditorDropdownMode.THRESHOLD_LEFT_ACTION: {
      return threshold.left.action.publicId;
    }
    case RuleEditorDropdownMode.THRESHOLD_LEFT_ATTRIBUTE: {
      return threshold.left.attributeId;
    }
    case RuleEditorDropdownMode.THRESHOLD_OPERATOR: {
      return leftDataType === 'reasoncodes'
        ? `codes_${threshold?.operator}`
        : threshold?.operator;
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ACTION: {
      return threshold.right.action.publicId;
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE: {
      return threshold.right.attributeId;
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_MULTISELECT:
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE: {
      return threshold.right.attributeValue === BEST_EFFORT_PARSING_DEFAULT_STRING
        ? '' : String(threshold.right.attributeValue);
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_DATEPICKER: {
      return formatDateTime(String(threshold.right.attributeValue), leftDataType);
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_REASONCODEGROUP: {
      return threshold.right.reasonCodeGroupId;
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_BOOLEAN: {
      return threshold.right.attributeValue;
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MAX:
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MIN: {
      const rightAttrValueString = String(threshold.right.attributeValue);
      const rightValueMinMax = rightAttrValueString.split(','); // [rightValueMin, rightValueMax]
      const index = RuleEditorDropdownMode.THRESHOLD_RIGHT_ATTRIBUTE_VALUE_RANGE_MIN === mode
        ? 0
        : 1;
      return rightValueMinMax[index];
    }
    case RuleEditorDropdownMode.THRESHOLD_RIGHT_TAGACTIONRESULT: {
      return String(threshold.right.leftTagActionResult);
    }
    case RuleEditorDropdownMode.GROUP_START: {
      return '(';
    }
    case RuleEditorDropdownMode.GROUP_END: {
      return ')';
    }
    case RuleEditorDropdownMode.RULE_OPERATOR_AND: {
      return 'and';
    }
    case RuleEditorDropdownMode.RULE_OPERATOR_OR: {
      return 'or';
    }
    default: {
      return undefined;
    }
  }
}

export const getCommaDelineatedValues = (str: string) => (
  str.split(COMMA_DELINEATOR)
);

export const getCommaDelineatedDisplayValues = (str: string) => (
  str.split(COMMA_DELINEATOR_DISPLAY)
);

export const getCommaDelineatedString = (values: string[]) => (
  values.join(COMMA_DELINEATOR)
);

export const getCommaDelineatedDisplayString = (values: string[]) => (
  values.join(COMMA_DELINEATOR_DISPLAY)
);

export const scrubTrailingSpaces = (commaDelineatedDisplayString: string) => (
  getCommaDelineatedString(
    getCommaDelineatedDisplayValues(commaDelineatedDisplayString),
  )
);

export const addTrailingSpaces = (commaDelineatedString: string) => (
  getCommaDelineatedDisplayString(
    getCommaDelineatedValues(commaDelineatedString),
  )
);

export function truncateLabel(label: string, lengthLimit: number) {
  return label.length >= lengthLimit
    ? `${label.substring(0, lengthLimit)}...`
    : label;
}

export const truncateLabelWithRemainder = (values: string[], lengthLimit: number) => {
  // scan through string values, remove values that go past our character limit
  let charCount = 0;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    const newLength = charCount + value.length + COMMA_DELINEATOR_DISPLAY.length;

    if (newLength >= lengthLimit) {
      // one big value case
      if (values.length === 1) {
        return truncateLabel(
          getCommaDelineatedDisplayString(values),
          lengthLimit,
        );
      }
      // only include string values in label that are below the length limit
      const valuesToInclude = values.slice(0, i);

      if (valuesToInclude.length === 0) {
        return truncateLabel(
          getCommaDelineatedDisplayString(values),
          lengthLimit,
        );
      }

      const remainder = values.length - valuesToInclude.length;
      const displayString = getCommaDelineatedDisplayString(valuesToInclude);

      return `${displayString}, ${remainder} more...`;
    }

    charCount = newLength;
  }
  return getCommaDelineatedDisplayString(values);
};

export function formatDateTime(value: string, leftDataType: string) {
  if (!value) return '';
  if (leftDataType === 'timestamp') {
    return dayjs.utc(value).format('MMM DD, YYYY h:mm A');
  }
  return dayjs(value).format('MMM DD, YYYY');
}

/**
 * Returns a list of actions that can be selected in threshold dropdowns without creating
 * cyclical dependencies. Matrix models can be optionally included or excluded.
 *
 * Filters applied:
 * 1. Excludes the source action (to prevent self-referential comparisons)
 * 2. Excludes outcome actions
 * 3. Optionally excludes matrix model actions (for RHS dynamic comparison)
 * 4. Excludes actions that would create cyclical dependencies
 * 5. Filters out service actions without attributes
 *
 * The resulting actions are sorted alphabetically by label.
 *
 * @param actions - All available actions in the workflow
 * @param lengthLimit - Maximum length for action name display
 * @param sourceAction - The action that owns this threshold (the threshold's parent)
 * @param nodeLineage - The lineage of nodes in the graph for cyclical dependency detection
 * @param services - Service definitions for attribute validation
 * @param excludeMatrixModels - Whether to exclude matrix models (for RHS dynamic comparison).
 *                               Default: false
 * @returns Array of dropdown options for actions that can be used in thresholds
 */
export const getNonCyclicalActions = (
  {
    actions, lengthLimit, sourceAction, nodeLineage, services, excludeMatrixModels = false,
  }: {
    actions: Record<string, Action>;
    lengthLimit: number;
    sourceAction: Action;
    nodeLineage: NodeLineage;
    services: ServicesByName;
    excludeMatrixModels?: boolean;
  }): DropdownOption[] => Object.entries(actions)
  // First filter: Exclude source action, outcomes, and optionally matrix models
  .filter(([, action]) => {
    if (sourceAction.publicId === action.publicId) return false;
    if (action.type === ActionType.OUTCOME) return false;
    if (excludeMatrixModels && isActionOfType(action, [
      ActionType.MATRIX_MODEL,
      ActionType.IMPORTED_MATRIX_MODEL,
    ])) {
      return false;
    }
    return true;
  })
  // Second filter: Check for cyclical dependencies and validate service eligibility
  .filter(([id, action]) => {
    let serviceEligible = true;
    // Ensure service actions have attributes to be usable in thresholds
    if (action?.type === ActionType.SERVICE) {
      const service = services[action.service.internalName];
      if (service) serviceEligible = serviceHasAttributes(service);
    }
    return canLinkToNode(nodeLineage, id) && serviceEligible;
  })
  // Transform actions into dropdown options
  .map(([id, action]) => {
    const actionName = getActionName(action);
    const nameWithColons = `${actionName}${action.type === 'service' ? ': ' : ''}`;
    return {
      label: `${getActionTypeText(action)}: ${actionName}`,
      value: id,
      meta: {
        shortenedLabel: truncateLabel(nameWithColons, lengthLimit),
      },
    };
  })
  // Sort alphabetically for consistent UI display
  .sort((a, b) => a.label.localeCompare(b.label));

export const canDynamicallyCompareActions = (
  leftAction: Action,
  rightAction: Action,
) => {
  const violatingConditons = [
    isActionOfType(leftAction, ActionType.INPUT) && isActionOfType(rightAction, ActionType.OUTPUT),
    isActionOfType(leftAction, ActionType.OUTPUT) && isActionOfType(rightAction, ActionType.INPUT),
    isActionOfType(leftAction, ActionType.OUTPUT) && isActionOfType(rightAction, ActionType.OUTPUT),
    leftAction.service?.internalName === 'IdentityElementVelocity' && isActionOfType(rightAction, ActionType.OUTPUT),
    isActionOfType(rightAction, ActionType.OUTPUT), // we temporarily block output actions from being compared to anything
  ];
  if (violatingConditons.includes(true)) return false;
  return true;
};

// Retrieves the maximum character length for any rule item label
// TODO: find a more robust way to compute this. the 20 was found by playing with
// values; it might not be desirable for all screen resolutions
export const computeRuleItemLengthLimit = (containerWidth: number) => (
  Math.floor(containerWidth / 20)
);
